import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { ForecastData, PrepItem, PrepSubItem, SchedulePrepData, UptData } from 'src/types/apps/managerSetup'

dayjs.extend(customParseFormat)

/** Schedule Prep AM: 12:00 AM – 11:59 AM (clock morning).
 * Harbour sales AM/PM split (5:00 AM – 4:00 PM) is separate and unchanged. */
export const AM_WINDOW_START_MIN = 0
export const AM_WINDOW_END_MIN = 11 * 60 + 59

/** Schedule Prep PM: 12:00 PM – 11:59 PM (clock afternoon/evening). */
export const PM_WINDOW_START_MIN = 12 * 60
export const PM_WINDOW_END_MIN = 23 * 60 + 59

export type PrepTimeFieldErrors = {
  am?: string
  pm?: string
  combine?: string
}

/** Parse "h:mm a" / "hh:mm a" style prep times to minutes from midnight. */
export function parsePrepTimeToMinutes(value?: string | null): number | null {
  const raw = (value ?? '').trim()
  if (!raw) return null

  const parsed = dayjs(raw, ['h:mm a', 'hh:mm a', 'h:mm A', 'hh:mm A'], true)
  if (!parsed.isValid()) return null

  return parsed.hour() * 60 + parsed.minute()
}

export function isAmInWindow(minutes: number): boolean {
  return minutes >= AM_WINDOW_START_MIN && minutes <= AM_WINDOW_END_MIN
}

export function isPmInWindow(minutes: number): boolean {
  return minutes >= PM_WINDOW_START_MIN && minutes <= PM_WINDOW_END_MIN
}

/**
 * PM must come after AM on the same clock day.
 * With clock windows (AM before noon, PM from noon), any in-window pair
 * already satisfies this; equal times are never valid.
 */
export function isPmAfterAmInCycle(amMinutes: number, pmMinutes: number): boolean {
  return pmMinutes > amMinutes
}

const MSG = {
  amWindow: 'AM must be between 12:00 am and 11:59 am',
  pmWindow: 'PM must be between 12:00 pm and 11:59 pm',
  pmAfterAm: 'PM must be after AM',
  equal: 'AM and PM cannot be the same time',
  invalid: 'Enter a valid time',
  combineAfterGroup: 'Must be at or after the section combine time',
  amAfterGroup: 'Must be at or after the section AM time',
  pmAfterGroup: 'Must be at or after the section PM time'
} as const

/**
 * True when item minutes are strictly before group minutes (same clock-day comparison).
 * Equality is valid. Does not invent overnight/cross-midnight semantics.
 */
export function isPrepTimeBeforeGroup(itemMinutes: number, groupMinutes: number): boolean {
  return itemMinutes < groupMinutes
}

/**
 * Validate one Schedule Prep row (section header or food item).
 * When combine is enabled, AM/PM window + ordering are skipped; only combine time is checked.
 * Ingredient rows may pass group* times so item time must be >= parent section start.
 */
export function validatePrepTimeRow(input: {
  amPrepTime?: string | null
  pmPrepTime?: string | null
  combinePrep?: boolean
  combinePrepTime?: string | null

  /** Section/group combine time — ingredient rows must be at or after this. */
  groupCombinePrepTime?: string | null

  /** Section/group AM start — ingredient AM must be at or after this. */
  groupAmPrepTime?: string | null

  /** Section/group PM start — ingredient PM must be at or after this. */
  groupPmPrepTime?: string | null
  hasAmPrep?: boolean
  hasPmPrep?: boolean
}): PrepTimeFieldErrors {
  const errors: PrepTimeFieldErrors = {}
  const combined = Boolean(input.combinePrep)

  if (combined) {
    const raw = (input.combinePrepTime ?? '').trim()
    const itemMin = raw ? parsePrepTimeToMinutes(raw) : null
    if (raw && itemMin === null) {
      errors.combine = MSG.invalid

      return errors
    }

    const groupRaw = (input.groupCombinePrepTime ?? '').trim()
    if (raw && groupRaw) {
      const groupMin = parsePrepTimeToMinutes(groupRaw)
      if (groupMin !== null && itemMin !== null && isPrepTimeBeforeGroup(itemMin, groupMin)) {
        errors.combine = MSG.combineAfterGroup
      }
    }

    return errors
  }

  const showAm = input.hasAmPrep !== false
  const showPm = input.hasPmPrep !== false
  const amRaw = (input.amPrepTime ?? '').trim()
  const pmRaw = (input.pmPrepTime ?? '').trim()
  const amMin = amRaw ? parsePrepTimeToMinutes(amRaw) : null
  const pmMin = pmRaw ? parsePrepTimeToMinutes(pmRaw) : null

  if (showAm && amRaw) {
    if (amMin === null) errors.am = MSG.invalid
    else if (!isAmInWindow(amMin)) errors.am = MSG.amWindow
  }

  if (showPm && pmRaw) {
    if (pmMin === null) errors.pm = MSG.invalid
    else if (!isPmInWindow(pmMin)) errors.pm = MSG.pmWindow
  }

  if (showAm && showPm && amMin !== null && pmMin !== null) {
    if (amMin === pmMin) {
      errors.pm = MSG.equal
    } else if (!errors.am && !errors.pm && !isPmAfterAmInCycle(amMin, pmMin)) {
      errors.pm = MSG.pmAfterAm
    }
  }

  if (!errors.am && showAm && amMin !== null) {
    const groupAmRaw = (input.groupAmPrepTime ?? '').trim()
    if (groupAmRaw) {
      const groupAmMin = parsePrepTimeToMinutes(groupAmRaw)
      if (groupAmMin !== null && isPrepTimeBeforeGroup(amMin, groupAmMin)) {
        errors.am = MSG.amAfterGroup
      }
    }
  }

  if (!errors.pm && showPm && pmMin !== null) {
    const groupPmRaw = (input.groupPmPrepTime ?? '').trim()
    if (groupPmRaw) {
      const groupPmMin = parsePrepTimeToMinutes(groupPmRaw)
      if (groupPmMin !== null && isPrepTimeBeforeGroup(pmMin, groupPmMin)) {
        errors.pm = MSG.pmAfterGroup
      }
    }
  }

  return errors
}

function rowHasErrors(errors: PrepTimeFieldErrors): boolean {
  return Boolean(errors.am || errors.pm || errors.combine)
}

function validateItemLike(
  item: PrepItem | PrepSubItem,
  parentCombined = false,
  groupTimes?: {
    combinePrepTime?: string | null
    amPrepTime?: string | null
    pmPrepTime?: string | null
  }
): boolean {
  const combined = parentCombined || Boolean(item.combinePrep)
  if (
    rowHasErrors(
      validatePrepTimeRow({
        amPrepTime: item.amPrepTime,
        pmPrepTime: item.pmPrepTime,
        combinePrep: combined,
        combinePrepTime: item.combinePrepTime,
        groupCombinePrepTime: combined ? groupTimes?.combinePrepTime : undefined,
        groupAmPrepTime: combined ? undefined : groupTimes?.amPrepTime,
        groupPmPrepTime: combined ? undefined : groupTimes?.pmPrepTime,
        hasAmPrep: item.hasAmPrep,
        hasPmPrep: item.hasPmPrep
      })
    )
  ) {
    return true
  }

  return false
}

/** True when any Schedule Prep section/item/sub-item has an invalid time. */
export function hasSchedulePrepTimeErrors(data: SchedulePrepData | null | undefined): boolean {
  if (!data?.sections?.length) return false

  for (const section of data.sections) {
    for (const item of section.items) {
      if (validateItemLike(item)) return true
      const parentCombined = Boolean(item.combinePrep)
      const groupTimes = {
        combinePrepTime: parentCombined ? item.combinePrepTime : undefined,
        amPrepTime: parentCombined ? undefined : item.amPrepTime,
        pmPrepTime: parentCombined ? undefined : item.pmPrepTime
      }
      for (const sub of item.subItems ?? []) {
        if (validateItemLike(sub, parentCombined, groupTimes)) return true
      }
    }
  }

  return false
}

// ─── Tab 1 / Tab 2 manager override (shared limits) ───────────────────────────

export const MANAGER_OVERRIDE_MAX = 99999.99
export const MANAGER_OVERRIDE_MAX_INTEGER_DIGITS = 5
export const UPT_OVERRIDE_MAX_DECIMALS = 4
export const UPT_MANAGER_OVERRIDE_MAX = 999999
export const UPT_MANAGER_OVERRIDE_MAX_INTEGER_DIGITS = 6
export const UPT_MANAGER_OVERRIDE_MAX_TOTAL_DIGITS = 6

export const FORECAST_MANAGER_OVERRIDE_MAX = MANAGER_OVERRIDE_MAX
export const FORECAST_MANAGER_OVERRIDE_MAX_INTEGER_DIGITS = MANAGER_OVERRIDE_MAX_INTEGER_DIGITS

/** Tab 1 Manager Override uses the same decimal-place rule as Tab 2 Override. */
export const FORECAST_MANAGER_OVERRIDE_MAX_DECIMALS = UPT_OVERRIDE_MAX_DECIMALS

/** Tab 1 override when unit is % — adjustment percent −100…100, max 2 dp. */
export const FORECAST_OVERRIDE_PERCENT_MIN = -100
export const FORECAST_OVERRIDE_PERCENT_MAX = 100
export const FORECAST_OVERRIDE_PERCENT_MAX_INTEGER_DIGITS = 3
export const FORECAST_OVERRIDE_PERCENT_MAX_DECIMALS = 2

const OVERRIDE_MSG = {
  negative: 'Must be 0 or greater',
  invalid: 'Enter a valid number',
  digits: 'Maximum 5 digits (up to 99,999)',
  max: 'Maximum 99,999',

  /** Percent unit only — must not mention dollar / 5-digit limits. */
  percentRange: 'Must be between -100 and 100',
  uptDigits: 'Maximum 6 digits total (up to 4 after the decimal)',
  uptMax: 'Maximum 6 digits total'
} as const

function countIntegerDigits(value: number): number {
  const intPart = Math.trunc(Math.abs(value))

  if (intPart === 0) return 1

  return String(intPart).length
}

function validateManagerOverrideValue(
  value: number | null | undefined,
  maxValue: number,
  maxIntegerDigits: number,
  maxLabel: string,
  digitsLabel: string
): string | undefined {
  if (value === undefined || value === null) return undefined
  if (!Number.isFinite(value)) return OVERRIDE_MSG.invalid
  if (value < 0) return OVERRIDE_MSG.negative
  if (countIntegerDigits(value) > maxIntegerDigits) return digitsLabel
  if (value > maxValue) return maxLabel

  return undefined
}

export function validateForecastManagerOverride(value: number | null | undefined): string | undefined {
  return validateManagerOverrideValue(
    value,
    MANAGER_OVERRIDE_MAX,
    MANAGER_OVERRIDE_MAX_INTEGER_DIGITS,
    OVERRIDE_MSG.max,
    OVERRIDE_MSG.digits
  )
}

/** Tab 1 Manager Override when the unit selector is %. Allows negatives (−100…100, ≤2 dp). */
export function validateForecastOverridePercent(value: number | null | undefined): string | undefined {
  if (value === undefined || value === null) return undefined
  if (!Number.isFinite(value)) return OVERRIDE_MSG.invalid
  if (countIntegerDigits(value) > FORECAST_OVERRIDE_PERCENT_MAX_INTEGER_DIGITS) {
    return OVERRIDE_MSG.percentRange
  }
  if (value < FORECAST_OVERRIDE_PERCENT_MIN || value > FORECAST_OVERRIDE_PERCENT_MAX) {
    return OVERRIDE_MSG.percentRange
  }

  const rounded = Math.round(value * 100) / 100
  if (Math.abs(value - rounded) > 1e-9) return OVERRIDE_MSG.invalid

  return undefined
}

export function validateUptManagerOverride(value: number | null | undefined): string | undefined {
  return validateManagerOverrideValue(
    value,
    UPT_MANAGER_OVERRIDE_MAX,
    UPT_MANAGER_OVERRIDE_MAX_INTEGER_DIGITS,
    OVERRIDE_MSG.uptMax,
    OVERRIDE_MSG.uptDigits
  )
}

function parseManagerOverrideInput(
  raw: string,
  maxValue: number,
  maxIntegerDigits: number,
  maxLabel: string,
  digitsLabel: string,
  maxDecimals = 2,
  maxTotalDigits?: number
): { value?: number; error?: string } {
  const trimmed = raw.trim()
  if (!trimmed) return { value: undefined }

  if (trimmed.startsWith('-') || trimmed.includes('-')) {
    return { error: OVERRIDE_MSG.negative }
  }

  const allowedPattern = new RegExp(`^\\d+(?:\\.\\d{0,${maxDecimals}})?$`)
  if (!allowedPattern.test(trimmed)) return { error: OVERRIDE_MSG.invalid }
  if (maxTotalDigits !== undefined && trimmed.replace('.', '').length > maxTotalDigits) {
    return { error: digitsLabel }
  }

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return { error: OVERRIDE_MSG.invalid }
  if (parsed < 0) return { error: OVERRIDE_MSG.negative }
  if (countIntegerDigits(parsed) > maxIntegerDigits) return { error: digitsLabel }
  if (parsed > maxValue) return { error: maxLabel }

  return { value: parsed }
}

/**
 * Shared keystroke filter for Tab 1 / Tab 2 override fields.
 * Allows empty, digits, and at most one `.` with up to `maxDecimals` places.
 * Rejects +, *, /, e, E, %, and other non-numeric syntax.
 * Leading `-` only when `allowNegative` is true (percent mode).
 */
export function isAllowedManagerOverrideDraft(
  raw: string,
  options: {
    maxIntegerDigits: number
    maxDecimals: number
    maxTotalDigits?: number
    allowNegative?: boolean
  }
): boolean {
  if (!raw) return true

  const { maxIntegerDigits, maxDecimals, maxTotalDigits, allowNegative } = options

  if (allowNegative) {
    if (raw === '-') return true
    const allowedPattern = new RegExp(`^-?\\d{0,${maxIntegerDigits}}(?:\\.\\d{0,${maxDecimals}})?$`)
    if (!allowedPattern.test(raw)) return false
    const withoutSign = raw.startsWith('-') ? raw.slice(1) : raw
    if (maxTotalDigits !== undefined && withoutSign.replace('.', '').length > maxTotalDigits) {
      return false
    }

    return true
  }

  const allowedPattern = new RegExp(`^\\d{0,${maxIntegerDigits}}(?:\\.\\d{0,${maxDecimals}})?$`)
  if (!allowedPattern.test(raw)) return false
  if (maxTotalDigits !== undefined && raw.replace('.', '').length > maxTotalDigits) return false

  return true
}

export function parseForecastManagerOverrideInput(raw: string) {
  return parseManagerOverrideInput(
    raw,
    MANAGER_OVERRIDE_MAX,
    MANAGER_OVERRIDE_MAX_INTEGER_DIGITS,
    OVERRIDE_MSG.max,
    OVERRIDE_MSG.digits,
    FORECAST_MANAGER_OVERRIDE_MAX_DECIMALS
  )
}

/** Tab 1 % unit — negatives allowed, −100…100, max 2 decimal places. */
export function parseForecastOverridePercentInput(raw: string): {
  value?: number
  error?: string
} {
  const trimmed = raw.trim()
  if (!trimmed) return { value: undefined }

  // Incomplete while typing (do not clear / commit yet).
  if (trimmed === '-' || /^-?\d+\.$/.test(trimmed)) {
    return {}
  }

  const allowedPattern = new RegExp(`^-?\\d+(?:\\.\\d{1,${FORECAST_OVERRIDE_PERCENT_MAX_DECIMALS}})?$`)
  if (!allowedPattern.test(trimmed)) return { error: OVERRIDE_MSG.invalid }

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return { error: OVERRIDE_MSG.invalid }

  const rangeError = validateForecastOverridePercent(parsed)
  if (rangeError) return { error: rangeError }

  return { value: parsed }
}

export function parseUptManagerOverrideInput(raw: string) {
  return parseManagerOverrideInput(
    raw,
    UPT_MANAGER_OVERRIDE_MAX,
    UPT_MANAGER_OVERRIDE_MAX_INTEGER_DIGITS,
    OVERRIDE_MSG.uptMax,
    OVERRIDE_MSG.uptDigits,
    UPT_OVERRIDE_MAX_DECIMALS,
    UPT_MANAGER_OVERRIDE_MAX_TOTAL_DIGITS
  )
}

export function hasForecastOverrideErrors(forecastData: ForecastData | null | undefined): boolean {
  if (!forecastData?.days?.length) return false

  return forecastData.days.some(d => validateForecastManagerOverride(d.managerOverride) !== undefined)
}

export function hasUptOverrideErrors(uptData: UptData | null | undefined): boolean {
  if (!uptData?.sections?.length) return false

  return uptData.sections.some(s => s.items.some(i => validateUptManagerOverride(i.override) !== undefined))
}
